/**
 * Sign-in screen for the Control Center.
 *
 * Development access uses synthetic accounts only. The screen states that plainly,
 * so nobody mistakes this for a production login: no real Zoho, Google or customer
 * credential is accepted or transmitted anywhere.
 */

import { FormEvent, useState } from "react";
import { backendBaseUrl, backendConfigured } from "@/api/client";
import { useSession } from "@/control/SessionContext";

export default function LoginPage() {
  const { signIn, loading, error } = useSession();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    // The provider owns the message because this component is unmounted while the
    // request is in flight (`loading` shows the gate's spinner). Setting state here
    // after an await would write to a dead component.
    try {
      await signIn(email, password);
    } catch {
      /* `signIn` records the failure; rethrowing stops here. */
    }
  }

  return (
    <div className="mx-auto max-w-md px-6 py-16" data-testid="control-login">
      <p className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
        MegaBooker AI Business OS
      </p>
      <h1 className="mt-3 text-2xl font-semibold">Control Center</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        G1a sintetičko okruženje. Koristite isključivo razvojne sintetičke račune;
        stvarni Zoho, Google i poslovni podaci nisu povezani.
      </p>

      {!backendConfigured() && (
        <p className="mt-4 rounded border border-destructive/40 bg-destructive/10 p-3 text-sm"
           data-testid="control-backend-missing">
          <code>REACT_APP_BACKEND_URL</code> nije postavljen, pa Control Center ne može
          dohvatiti API. Postavite ga u zaštićenim postavkama okruženja.
        </p>
      )}

      <form className="mt-6 space-y-4" onSubmit={onSubmit} data-testid="control-login-form">
        <label className="block">
          <span className="text-sm font-medium">Email</span>
          <input
            type="email"
            required
            autoComplete="username"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            className="mt-1 w-full rounded border border-border bg-background px-3 py-2 text-sm"
            data-testid="control-login-email"
          />
        </label>
        <label className="block">
          <span className="text-sm font-medium">Lozinka</span>
          <input
            type="password"
            required
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            className="mt-1 w-full rounded border border-border bg-background px-3 py-2 text-sm"
            data-testid="control-login-password"
          />
        </label>

        <button
          type="submit"
          disabled={loading || !backendConfigured()}
          className="w-full rounded bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
          data-testid="control-login-submit"
        >
          {loading ? "Prijava…" : "Prijavi se"}
        </button>
      </form>

      {error && (
        <p className="mt-4 text-sm text-destructive" role="alert"
           data-testid="control-login-error">
          {error}
        </p>
      )}

      <p className="mt-8 text-xs text-muted-foreground">
        API: <code>{backendBaseUrl() || "(nije postavljen)"}</code>
      </p>
    </div>
  );
}
